import { useContext } from 'react';
import { Grid, Stack, Switch, Tooltip } from '@mui/material';
import { ArrowLeft } from '@phosphor-icons/react';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik } from 'formik';
import { Header } from '../../components/Header';
import { Button } from '../../components/Button';
import { LoadingScreen } from '../../components/LoadingScreen';
import { Project } from '../../types/ProjectType';
import { UserDataContext } from '../../providers/userData';
import { useTheme } from '../../providers/theme';
import { TextComponent } from '../../components/TextComponent';
import { InputComponent } from '../../components/InputComponent';
import { ProjectPageSchema } from './ProjectPageSchema';
import { InputContainer, SaveButtonContainer } from './styles';
import { useTranslation } from 'react-i18next';

export type ProjectProps = {
  projects?: Project;
  isLoading?: boolean;
  onSave: (values: Project) => void;
}

export function ProjectPage({ isLoading, onSave }: ProjectProps) {
  const navigate = useNavigate();
  const { contractId } = useParams();
  const { userData } = useContext(UserDataContext);
  const { theme } = useTheme();
  const { t } = useTranslation();

  return (
    <>
      {isLoading ? (
        <LoadingScreen isVisible={isLoading} />
      ) : (
        <>
          <Header />

          <Grid style={{ display: 'flex', alignItems: 'center', flexDirection: 'row', gap: 30, marginLeft: 100, marginTop: 30 }}>
            <Tooltip title='Visualizar Projetos'>
              <ArrowLeft onClick={() => navigate(`/contracts/${contractId}/projects`)} size={30} color={theme.colors.main} cursor='pointer' />
            </Tooltip>
            <TextComponent
              fontWeight='bold'
            >
              {t('projects-page-header')}
            </TextComponent>
          </Grid>

          <Formik
            initialValues={{
              title: '',
              description: '',
              is_active: false,
              owner_id: userData.userEmail || ''
            }}
            validationSchema={ProjectPageSchema}
            validateOnChange={true}
            validateOnMount={true}
            onSubmit={values => onSave(values)}
          >
            {({ errors, touched, handleSubmit, handleChange, values }) => (
              <>
                <form onSubmit={handleSubmit}>
                  <Grid sx={{ width: '400px', marginLeft: 12.2, marginTop: '30px', display: 'flex', flexDirection: 'column' }}>
                    <InputContainer>
                      <TextComponent
                        fontWeight='bold'
                        fontSize='14px'
                      >
                        {t('projects-table-title')}
                      </TextComponent>
                      <InputComponent
                        id='title'
                        type='text'
                        value={values.title}
                        onChange={handleChange}
                        placeholder='ex: PAC 700'
                      />
                      {errors.title && touched.title ? (
                        <div>
                          <span style={{ fontSize: '12px' }}>{errors.title}</span>
                        </div>
                      ) : null}
                    </InputContainer>

                    <InputContainer>
                      <TextComponent
                        fontWeight='bold'
                        fontSize='14px'
                      >
                        {t('projects-table-description')}
                      </TextComponent>
                      <InputComponent
                        id='description'
                        type='text'
                        value={values.description}
                        onChange={handleChange}
                        placeholder='ex: Projeto para estudos'
                      />
                      {errors.description && touched.description ? (
                        <div>
                          <span style={{ fontSize: '12px' }}>{errors.description}</span>
                        </div>
                      ) : null}
                    </InputContainer>

                    <InputContainer>
                      <TextComponent
                        fontWeight='bold'
                        fontSize='14px'
                      >
                        {t('projects-table-active')}
                      </TextComponent>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Switch id='is_active' name='is_active' value={values.is_active} onChange={handleChange} color='warning' />
                        <span>{t('projects-table-active')}</span>
                      </Stack>
                    </InputContainer>

                    <InputContainer>
                      <TextComponent
                        fontWeight='bold'
                        fontSize='14px'
                      >
                        {t('projects-table-manager')}
                      </TextComponent>
                      <InputComponent
                        id='owner_id'
                        type='text'
                        value={userData.userEmail}
                        onChange={handleChange}
                        placeholder={t('projects-table-email-placeholder')}
                        readOnly
                      />
                      {errors.owner_id && touched.owner_id ? (
                        <div>
                          <span style={{ fontSize: '12px' }}>{errors.owner_id}</span>
                        </div>
                      ) : null}
                    </InputContainer>
                  </Grid>

                  <SaveButtonContainer>
                    <Button
                      id='save'
                      type='submit'
                      color={theme.colors.main}
                      sxhover={theme.colors.hoverMain}
                    >
                      {t('projects-table-save')}
                    </Button>
                  </SaveButtonContainer>
                </form>
              </>
            )}
          </Formik>
        </>
      )}

    </>
  );
}
